import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email exists, a reset link has been sent.");
    } catch (e2) {
      const msg = formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grid scanlines">
      <Navbar />
      <main className="mx-auto max-w-md px-6 py-16">
        <div className="border border-border bg-card" data-testid="forgot-password-card">
          <div className="border-b border-border px-6 py-4 flex items-center justify-between">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">// auth · recover</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400">reset link</div>
          </div>

          {sent ? (
            <div className="p-6 space-y-5" data-testid="forgot-password-sent">
              <h1 className="font-display font-bold text-3xl">Check your inbox.</h1>
              <p className="text-sm text-muted-foreground font-mono">
                {">"} If an account exists for <span className="text-primary">{email}</span>, we've sent a
                reset link. The link expires in 30 minutes.
              </p>
              <div className="border border-border/60 bg-muted/20 p-3 font-mono text-[11px] text-muted-foreground">
                {">"} didn't get it? check spam, or resend below.
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-none font-mono uppercase tracking-widest"
                  onClick={() => setSent(false)}
                  data-testid="forgot-password-resend-button"
                >
                  Send again
                </Button>
                <Link to="/login" className="flex-1" data-testid="forgot-password-back-to-login-link">
                  <Button className="w-full rounded-none font-mono uppercase tracking-widest">
                    Back to login
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6 space-y-5" data-testid="forgot-password-form">
              <div>
                <h1 className="font-display font-bold text-3xl">Forgot password?</h1>
                <p className="mt-2 text-sm text-muted-foreground font-mono">
                  {">"} enter your email — we'll send you a reset link
                </p>
              </div>

              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-widest" htmlFor="email">email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="rounded-none font-mono"
                  placeholder="you@team.com"
                  data-testid="forgot-password-email-input"
                />
              </div>

              {err && (
                <div className="border border-destructive/40 bg-destructive/10 text-destructive font-mono text-xs px-3 py-2" data-testid="forgot-password-error">
                  ! {err}
                </div>
              )}

              <Button
                type="submit"
                disabled={busy}
                className="w-full rounded-none font-mono uppercase tracking-widest h-11"
                data-testid="forgot-password-submit-button"
              >
                {busy ? "Sending…" : "Send reset link"}
              </Button>

              <div className="font-mono text-xs text-muted-foreground text-center">
                {">"} remembered it?{" "}
                <Link to="/login" className="text-primary underline-offset-4 hover:underline" data-testid="forgot-password-to-login-link">
                  back to login
                </Link>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
